import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {CrookedStar, FourPointStar} from "../assets";
import Step1 from "../components/beranda/readiness_Check/step1";
import Step2 from "../components/beranda/readiness_Check/step2";
import Step3 from "../components/beranda/readiness_Check/step3";
import Step4 from "../components/beranda/readiness_Check/step4";
import {
  getReadinessPlacementResult,
  getReadinessReport,
  getScholarships,
  submitReadinessChecker,
} from "../hooks/useApi";

const READINESS_SESSION_ID_KEY = "readiness_session_id";
const READINESS_PAYLOAD_KEY = "readiness_payload_key";

const ReadinessChecker = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [scholarships, setScholarships] = useState([]);
  const [isLoadingSetup, setIsLoadingSetup] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [reportData, setReportData] = useState(null);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(READINESS_SESSION_ID_KEY) || "");
  const [lastPayloadKey, setLastPayloadKey] = useState(() => sessionStorage.getItem(READINESS_PAYLOAD_KEY) || "");

  const [answers, setAnswers] = useState({
    entryPath: "direct",
    placementResultId: "",
    targetType: "",
    targetId: "",
    targetName: "",
    degreeLevel: "",
    hasTakenIelts: true,
    ieltsOverall: "",
    englishUsageFrequency: "",
    learningBackground: "",
    learningDurationMonths: "",
  });

  const isPathB = answers.entryPath === "from_placement";

  useEffect(() => {
    const setupChecker = async () => {
      setIsLoadingSetup(true);
      setLoadError("");

      try {
        const data = await getScholarships();
        setScholarships(data);

        const fromState = location.state?.entry_path === "from_placement";
        const fromQuery = searchParams.get("entry_path") === "from_placement";
        const pathFromPlacement = fromState || fromQuery;

        const placementIdFromState = location.state?.placement_result_id;
        const placementIdFromQuery = searchParams.get("placement_result_id");
        const placementResultId = placementIdFromState || placementIdFromQuery || "";

        if (!pathFromPlacement || !placementResultId) {
          setAnswers((prev) => ({
            ...prev,
            entryPath: "direct",
            hasTakenIelts: true,
            placementResultId: "",
          }));
          return;
        }

        const placementData = await getReadinessPlacementResult(placementResultId);
        setAnswers((prev) => ({
          ...prev,
          entryPath: "from_placement",
          hasTakenIelts: false,
          placementResultId: String(placementData.placement_result_id),
          ieltsOverall: String(placementData.estimated_ielts_band),
        }));
      } catch (err) {
        setLoadError("Gagal memuat data readiness checker. Coba refresh halaman.");
      } finally {
        setIsLoadingSetup(false);
      }
    };

    setupChecker();
  }, [location.state, searchParams]);

  useEffect(() => {
    const loadExistingReport = async () => {
      if (!sessionId || !lastPayloadKey) {
        return;
      }

      try {
        const data = await getReadinessReport(sessionId);
        setReportData(data);
      } catch (err) {
        sessionStorage.removeItem(READINESS_SESSION_ID_KEY);
        sessionStorage.removeItem(READINESS_PAYLOAD_KEY);
        setSessionId("");
        setLastPayloadKey("");
      }
    };

    loadExistingReport();
  }, [sessionId, lastPayloadKey]);

  const selectedScholarship = useMemo(
    () => scholarships.find((item) => String(item.id) === String(answers.targetId)),
    [scholarships, answers.targetId]
  );

  const buildPayload = () => ({
    entry_path: answers.entryPath,
    placement_result_id: answers.entryPath === "from_placement" ? Number(answers.placementResultId) : null,
    target_type: answers.targetType,
    target_id: Number(answers.targetId),
    degree_level: answers.degreeLevel,
    has_taken_ielts: answers.hasTakenIelts,
    ielts_score_input: answers.hasTakenIelts ? Number(answers.ieltsOverall) : null,
    english_usage_freq: answers.englishUsageFrequency,
    learning_background: answers.learningBackground,
    learning_duration_months: Number(answers.learningDurationMonths),
  });

  const handleSubmitReadiness = async () => {
    setSubmitError("");

    const payload = buildPayload();
    const payloadKey = JSON.stringify(payload);

    if (reportData && payloadKey === lastPayloadKey) {
      return reportData;
    }

    setIsSubmitting(true);

    try {
      const response = await submitReadinessChecker(payload);
      setReportData(response);
      setSessionId(String(response.session_id));
      setLastPayloadKey(payloadKey);
      sessionStorage.setItem(READINESS_SESSION_ID_KEY, String(response.session_id));
      sessionStorage.setItem(READINESS_PAYLOAD_KEY, payloadKey);
      return response;
    } catch (err) {
      setSubmitError("Submit readiness checker gagal. Periksa data lalu coba lagi.");
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const goNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const goBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const renderStep = () => {
    if (currentStep === 1) {
      return (
        <Step1
          currentStep={currentStep}
          totalSteps={totalSteps}
          answers={answers}
          setAnswers={setAnswers}
          scholarships={scholarships}
          onNext={goNext}
        />
      );
    }

    if (currentStep === 2) {
      return (
        <Step2
          currentStep={currentStep}
          totalSteps={totalSteps}
          answers={answers}
          setAnswers={setAnswers}
          isPathB={isPathB}
          onBack={goBack}
          onNext={goNext}
        />
      );
    }

    if (currentStep === 3) {
      return (
        <Step3
          currentStep={currentStep}
          totalSteps={totalSteps}
          answers={answers}
          setAnswers={setAnswers}
          onBack={goBack}
          onNext={goNext}
        />
      );
    }

    if (currentStep === 4) {
      return (
        <Step4
          currentStep={currentStep}
          totalSteps={totalSteps}
          answers={answers}
          selectedScholarship={selectedScholarship}
          reportData={reportData}
          isSubmitting={isSubmitting}
          submitError={submitError}
          onSubmit={handleSubmitReadiness}
          onBack={goBack}
        />
      );
    }

    return null;
  };

  return (
    <section className="st-surface-cream relative overflow-hidden px-4 py-5 sm:px-6 sm:py-7 lg:px-8">
      <div className="mx-auto w-full max-w-5xl">
        <div className="relative mx-auto w-full max-w-[860px]">
          <div className="pointer-events-none absolute inset-0 z-0 hidden sm:block" aria-hidden="true">
            <img
              src={FourPointStar}
              alt=""
              className="absolute left-[-84px] top-[72px] w-[108px] md:left-[-96px] md:w-[124px]"
              loading="lazy"
            />
            <img
              src={CrookedStar}
              alt=""
              className="absolute bottom-[48px] right-[-92px] w-[136px] md:right-[-104px] md:w-[150px]"
              loading="lazy"
            />
          </div>

          <div className="relative px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
            {isLoadingSetup ? (
              <p className="st-font-body text-[16px] text-[#4B4B4B]">Memuat data readiness checker...</p>
            ) : loadError ? (
              <p className="st-font-body text-[16px] text-[#BB230B]">{loadError}</p>
            ) : (
              renderStep()
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReadinessChecker;
